import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MessageCircle, Send, Loader2, X, Plus, Swords } from 'lucide-react';
import { getRoomTheme } from '@/config/roomThemes';
import FeatureBadge from '@/components/FeatureBadge';
import useStreamingChat from '@/hooks/useStreamingChat';

/* ──────────────────────────────────
   3D Room Interior — Immersive gallery
   ────────────────────────────────── */
const RoomInterior = ({ room, minds = [], onBack, onAddMind, onStartDebate, isLoading }) => {
  const [chatMind, setChatMind] = useState(null);
  const [showDebateSetup, setShowDebateSetup] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const containerRef = useRef(null);
  const theme = getRoomTheme(room?.id) || { accent: '#B8860B', accentLight: '#DAA520', surface: '#F2EFE9', gradient: 'linear-gradient(135deg, #FDFCFB, #F2EFE9)', portraitFrame: 'gilded', bgImage: '' };

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePos({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
  }, []);

  // Parallax values
  const px = (mousePos.x - 0.5) * 2;
  const py = (mousePos.y - 0.5) * 2;

  return (
    <div ref={containerRef} data-testid="room-interior" className="relative min-h-screen overflow-hidden" style={{ background: theme.gradient }} onMouseMove={handleMouseMove}>

      {/* Background wall with parallax image */}
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${theme.bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08,
          filter: 'saturate(0.5) blur(1px)',
          transform: `translate(${px * -8}px, ${py * -5}px) scale(1.05)`,
          transition: 'transform 0.6s ease-out',
        }}
      />

      {/* Room gradient overlay */}
      <div className="absolute inset-0" style={{ background: theme.gradient, opacity: 0.85 }} />

      {/* 3D architectural elements — side walls */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Left wall perspective */}
        <div className="absolute left-0 top-0 bottom-0 w-[15%]"
          style={{
            background: `linear-gradient(to right, ${theme.accent}08, transparent)`,
            transform: `translateX(${px * -5}px)`,
            transition: 'transform 0.5s ease-out',
          }}
        >
          {/* Wall molding lines */}
          <div className="absolute top-[15%] right-0 w-px h-[70%]" style={{ background: `${theme.accent}12` }} />
          <div className="absolute top-[20%] right-4 w-px h-[60%]" style={{ background: `${theme.accent}06` }} />
        </div>

        {/* Right wall perspective */}
        <div className="absolute right-0 top-0 bottom-0 w-[15%]"
          style={{
            background: `linear-gradient(to left, ${theme.accent}08, transparent)`,
            transform: `translateX(${px * 5}px)`,
            transition: 'transform 0.5s ease-out',
          }}
        >
          <div className="absolute top-[15%] left-0 w-px h-[70%]" style={{ background: `${theme.accent}12` }} />
          <div className="absolute top-[20%] left-4 w-px h-[60%]" style={{ background: `${theme.accent}06` }} />
        </div>

        {/* Ceiling beam */}
        <div className="absolute top-0 left-[10%] right-[10%] h-10"
          style={{
            background: `linear-gradient(to bottom, ${theme.accent}06, transparent)`,
            borderBottom: `1px solid ${theme.accent}08`,
          }}
        />

        {/* Floor reflection */}
        <div className="absolute bottom-0 left-0 right-0 h-[20%]"
          style={{ background: `linear-gradient(to top, ${theme.accent}06, transparent)` }}
        />
      </div>

      {/* Floating light particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 3 + Math.random() * 4,
              height: 3 + Math.random() * 4,
              left: `${15 + Math.random() * 70}%`,
              top: `${10 + Math.random() * 60}%`,
              background: `${theme.accent}20`,
              filter: 'blur(1px)',
            }}
            animate={{
              y: [0, -20 - Math.random() * 30, 0],
              x: [0, (Math.random() - 0.5) * 20, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{ duration: 4 + Math.random() * 4, repeat: Infinity, delay: Math.random() * 3 }}
          />
        ))}
      </div>

      {/* Warm ambient glow */}
      <div className="absolute top-[5%] left-1/2 -translate-x-1/2 w-[700px] h-[350px]"
        style={{
          background: `radial-gradient(ellipse, ${theme.accent}08 0%, transparent 55%)`,
          transform: `translate(${px * 15}px, ${py * 8}px)`,
          transition: 'transform 0.5s ease-out',
        }}
      />

      <div className="paper-texture" />

      {/* Header */}
      <motion.header className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <button data-testid="back-to-halls-btn" onClick={onBack} className="flex items-center gap-2 font-ui text-xs sm:text-sm tracking-wide transition-colors cursor-pointer group" style={{ color: '#8C8680' }}
          onMouseEnter={e => e.currentTarget.style.color = '#1A1512'} onMouseLeave={e => e.currentTarget.style.color = '#8C8680'}>
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="hidden sm:inline">Back to Halls</span>
        </button>
        <button data-testid="debate-btn"
          onClick={() => setShowDebateSetup(true)}
          className="flex items-center gap-2 px-4 py-2 font-ui text-xs tracking-wider uppercase transition-all cursor-pointer"
          style={{ background: '#1A1512', color: '#FDFCFB', boxShadow: '0 4px 12px rgba(26,21,18,0.12)' }}
        >
          <Swords className="w-3.5 h-3.5" />
          <span>Debate</span>
          <FeatureBadge feature="oxfordDebate" compact />
        </button>
      </motion.header>

      {/* Room title with depth */}
      <motion.div className="relative z-10 text-center mb-6 sm:mb-10 px-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
        <p className="font-ui text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2" style={{ color: theme.accent }}>{minds.length} minds in residence</p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-wide" style={{ color: '#1A1512' }}>{theme.name}</h1>
        <p className="font-body text-sm sm:text-base mt-2" style={{ color: '#8C8680' }}>&ldquo;{theme.tagline}&rdquo;</p>
        <motion.div className="h-px mx-auto mt-4" style={{ background: `linear-gradient(to right, transparent, ${theme.accent}40, transparent)` }} initial={{ width: 0 }} animate={{ width: 100 }} transition={{ duration: 0.8, delay: 0.2 }} />
      </motion.div>

      {/* 3D Gallery Wall */}
      <div className="relative z-10 container mx-auto px-4 sm:px-8 pb-16"
        style={{ perspective: '1200px', perspectiveOrigin: `${45 + mousePos.x * 10}% 50%` }}>
        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.accent }} /></div>
        ) : (
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 md:gap-10 max-w-4xl mx-auto"
            style={{
              transformStyle: 'preserve-3d',
              transform: `rotateY(${(mousePos.x - 0.5) * 3}deg) rotateX(${(mousePos.y - 0.5) * -2}deg)`,
              transition: 'transform 0.4s ease-out',
            }}
          >
            {minds.map((mind, i) => (
              <Portrait3D key={mind.id} mind={mind} theme={theme} index={i} onClick={() => setChatMind(mind)} mousePos={mousePos} />
            ))}
            <AddMindCard theme={theme} index={minds.length} onClick={onAddMind} />
          </motion.div>
        )}
      </div>

      <AnimatePresence>{chatMind && <ChatPanel mind={chatMind} theme={theme} onClose={() => setChatMind(null)} />}</AnimatePresence>
      <AnimatePresence>{showDebateSetup && <DebateSetup minds={minds} room={room} theme={theme} onClose={() => setShowDebateSetup(false)} onStart={onStartDebate} />}</AnimatePresence>
    </div>
  );
};

/* ──────────────────────────────────
   3D Portrait — Hangs on gallery wall
   ────────────────────────────────── */
const FRAME_CLASSES = { marble: 'frame-marble', holographic: 'frame-holographic', gilded: 'frame-gilded' };

const Portrait3D = ({ mind, theme, index, onClick, mousePos }) => {
  const [hovered, setHovered] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Each portrait at slightly different Z for depth
  const zOffset = 5 + (index % 3) * 3;

  return (
    <motion.div
      data-testid={`mind-portrait-${mind.id}`}
      className="cursor-pointer group"
      initial={{ opacity: 0, y: 40, rotateY: -10 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ delay: 0.1 + index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <motion.div
        className={`relative overflow-hidden ${FRAME_CLASSES[theme.portraitFrame] || 'frame-gilded'}`}
        style={{ aspectRatio: '3/4', transformStyle: 'preserve-3d' }}
        animate={{
          translateZ: hovered ? 35 : zOffset,
          rotateY: hovered ? (mousePos.x - 0.5) * 6 : 0,
          rotateX: hovered ? (mousePos.y - 0.5) * -4 : 0,
          y: hovered ? -12 : 0,
          boxShadow: hovered
            ? `0 35px 60px -15px rgba(0,0,0,0.2), 0 0 30px ${theme.accent}15`
            : '0 20px 40px -10px rgba(0,0,0,0.08)',
        }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
      >
        {mind.portrait && (
          <img
            src={mind.portrait}
            alt={mind.name}
            className="w-full h-full object-cover"
            style={{ transition: 'transform 0.7s ease', transform: hovered ? 'scale(1.06)' : 'scale(1)' }}
            onLoad={() => setLoaded(true)}
            loading="lazy"
          />
        )}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center animate-shimmer">
            <span className="font-display text-3xl" style={{ color: `${theme.accent}30` }}>{mind.name[0]}</span>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to top, rgba(26,21,18,${hovered ? 0.75 : 0.35}) 0%, transparent ${hovered ? '50%' : '30%'})`,
          transition: 'all 0.5s ease',
        }} />

        {/* Name on portrait */}
        <motion.div className="absolute bottom-0 left-0 right-0 p-3" animate={{ opacity: hovered ? 1 : 0.5, y: hovered ? 0 : 4 }}>
          <p className="font-display text-sm sm:text-base text-white truncate">{mind.name}</p>
          <p className="text-[10px] sm:text-xs text-white/50 truncate">{mind.title}</p>
        </motion.div>

        {/* Chat badge */}
        <motion.div
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: theme.accent, boxShadow: `0 4px 12px ${theme.accent}40` }}
          animate={{ scale: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <MessageCircle className="w-4 h-4 text-white" />
        </motion.div>
      </motion.div>

      {/* Museum label plaque */}
      <motion.div
        className="text-center mt-3"
        style={{ transformStyle: 'preserve-3d', transform: `translateZ(${zOffset / 2}px)` }}
        animate={{ y: hovered ? -3 : 0 }}
      >
        <p className="font-display text-xs sm:text-sm" style={{ color: '#1A1512' }}>{mind.name}</p>
        <p className="font-body text-[10px] sm:text-xs" style={{ color: '#8C8680' }}>{mind.era}</p>
      </motion.div>
    </motion.div>
  );
};

/* ──────────────────────────────────
   Add Mind Card
   ────────────────────────────────── */
const AddMindCard = ({ theme, index, onClick }) => (
  <motion.div
    data-testid="add-mind-btn"
    className="cursor-pointer"
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 + index * 0.1, duration: 0.6 }}
    onClick={onClick}
  >
    <motion.div
      className="relative overflow-hidden flex flex-col items-center justify-center gap-3"
      style={{ aspectRatio: '3/4', border: '2px dashed #E6E1DB', borderRadius: '6px', background: 'rgba(242,239,233,0.25)' }}
      whileHover={{ scale: 1.04, y: -10, borderColor: theme.accent, boxShadow: `0 20px 40px -10px rgba(0,0,0,0.08)` }}
      whileTap={{ scale: 0.97 }}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ border: `1.5px dashed ${theme.accent}35` }}>
        <Plus className="w-5 h-5" style={{ color: `${theme.accent}55` }} />
      </div>
      <span className="font-ui text-[10px] tracking-wider uppercase" style={{ color: '#8C8680' }}>Summon Mind</span>
    </motion.div>
  </motion.div>
);

/* ──────────────────────────────────
   Chat Panel
   ────────────────────────────────── */
const ChatPanel = ({ mind, theme, onClose }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const { streamingText, isStreaming, sendMessage: sendStreamingMessage } = useStreamingChat();
  const scrollRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, isStreaming, streamingText]);

  const sendMessage = async () => {
    if (!message.trim() || isStreaming || sendingRef.current) return;
    sendingRef.current = true;
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: message, id: Date.now() }]);
    const text = message;
    setMessage('');
    try {
      const data = await sendStreamingMessage({ mindId: mind.id, message: text, history });
      setMessages(prev => [...prev, { role: 'assistant', content: data.content, id: Date.now() + 1 }]);
    } catch (error) { setMessages(prev => [...prev, { role: 'assistant', content: error?.status === 429 ? error.message : 'I seem lost in thought. Please try again.', id: Date.now() + 1 }]); }
    finally { sendingRef.current = false; }
  };

  return (
    <motion.div data-testid="chat-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      style={{ background: 'rgba(26,21,18,0.25)', backdropFilter: 'blur(20px)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>

      <motion.div data-testid="chat-panel" className="w-full max-w-2xl h-[85vh] sm:h-[80vh] flex flex-col overflow-hidden"
        style={{ background: 'rgba(253,252,251,0.95)', backdropFilter: 'blur(24px)', border: '1px solid rgba(230,225,219,0.8)', borderRadius: '20px', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.6) inset' }}
        initial={{ scale: 0.92, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 30, opacity: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        onClick={e => e.stopPropagation()}>

        {/* Header with portrait */}
        <div className="flex items-center justify-between p-4 sm:p-5" style={{ borderBottom: '1px solid #E6E1DB', background: 'rgba(242,239,233,0.4)' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-14 rounded-lg overflow-hidden shadow-md" style={{ border: `2px solid ${theme.accent}30` }}>
              {mind.portrait ? <img src={mind.portrait} alt={mind.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold" style={{ background: theme.surface, color: theme.accent }}>{mind.name[0]}</div>}
            </div>
            <div>
              <h3 className="font-display text-base sm:text-lg" style={{ color: '#1A1512' }}>{mind.name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-body text-[10px] sm:text-xs" style={{ color: '#8C8680' }}>{mind.title}</p>
                <FeatureBadge feature="personaChat" compact />
              </div>
            </div>
          </div>
          <button data-testid="chat-close-btn" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-[#E6E1DB]" style={{ color: '#8C8680' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-20 h-24 rounded-xl mb-4 overflow-hidden shadow-lg" style={{ border: `2px solid ${theme.accent}20` }}>
                {mind.portrait && <img src={mind.portrait} alt={mind.name} className="w-full h-full object-cover" />}
              </div>
              <p className="font-display text-lg" style={{ color: '#1A1512' }}>Speak with {mind.name}</p>
              <p className="font-body text-sm mt-1" style={{ color: '#B5AFA8' }}>Ask anything you wish to know</p>
            </div>
          )}
          {messages.map(msg => (
            <motion.div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              {msg.role === 'user' ? (
                <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-5 py-3" style={{ background: '#1A1512', color: '#FDFCFB' }}>
                  <p className="text-sm leading-relaxed font-body">{msg.content}</p>
                </div>
              ) : (
                <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-5 py-3" style={{ background: '#F2EFE9', color: '#1A1512', border: '1px solid #E6E1DB' }}>
                  <p className="text-sm leading-relaxed font-body">{msg.content}</p>
                </div>
              )}
            </motion.div>
          ))}
          {isStreaming && streamingText && (
            <motion.div className="flex justify-start" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-5 py-3" style={{ background: '#F2EFE9', color: '#1A1512', border: '1px solid #E6E1DB' }}>
                <p className="text-sm leading-relaxed font-body">{streamingText}</p>
              </div>
            </motion.div>
          )}
          {isStreaming && !streamingText && (
            <motion.div className="flex justify-start" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="rounded-2xl rounded-tl-sm px-5 py-3" style={{ background: '#F2EFE9', border: '1px solid #E6E1DB' }}>
                <p className="text-xs font-body italic flex items-center gap-2" style={{ color: '#8C8680' }}>
                  {mind.thinking_message || `${mind.name} is reflecting...`}
                  <Loader2 className="w-3 h-3 animate-spin" />
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 sm:p-5" style={{ borderTop: '1px solid #E6E1DB' }}>
          <div className="flex gap-2">
            <input data-testid="chat-input" value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder={`Ask ${mind.name}...`}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-body outline-none transition-all"
              style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#1A1512' }}
              onFocus={e => e.target.style.borderColor = theme.accent} onBlur={e => e.target.style.borderColor = '#E6E1DB'}
            />
            <button data-testid="chat-send-btn" onClick={sendMessage} disabled={!message.trim() || isStreaming}
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-all cursor-pointer"
              style={{ background: '#1A1512', opacity: message.trim() && !isStreaming ? 1 : 0.3, boxShadow: '0 4px 12px rgba(26,21,18,0.15)' }}>
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ──────────────────────────────────
   Debate Setup — The Oxford Chamber
   ────────────────────────────────── */
const DebateSetup = ({ minds, room, theme, onClose, onStart }) => {
  const [mind1, setMind1] = useState(null);
  const [mind2, setMind2] = useState(null);
  const [topic, setTopic] = useState('');
  const available2 = minds.filter(m => m.id !== mind1?.id);

  const handleStart = () => {
    if (!mind1 || !mind2 || !topic.trim()) return;
    onStart(mind1, mind2, topic);
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(26,21,18,0.3)', backdropFilter: 'blur(16px)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="w-full max-w-3xl overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #F5F7F6, #EDECEA)', border: '1px solid #D6D0C8', borderRadius: '16px', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.15)' }}
        initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
        onClick={e => e.stopPropagation()}>
        <div className="px-6 sm:px-8 py-4" style={{ background: 'linear-gradient(135deg, #2F3E46, #354F52)', borderBottom: '3px solid #B8860B' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg sm:text-xl tracking-wide text-white flex flex-wrap items-center gap-2"><Swords className="w-5 h-5" /> The Oxford Chamber <FeatureBadge feature="oxfordDebate" compact /></h2>
              <p className="font-body text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Arrange an intellectual debate</p>
            </div>
            <button onClick={onClose} className="cursor-pointer text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs font-ui mb-2 tracking-wider uppercase" style={{ color: '#8C8680' }}>Proposer</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {minds.map(m => (
                  <MindOption key={m.id} mind={m} selected={mind1?.id === m.id} accent={theme.accent} onClick={() => setMind1(m)} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-ui mb-2 tracking-wider uppercase" style={{ color: '#8C8680' }}>Opposition</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {available2.map(m => (
                  <MindOption key={m.id} mind={m} selected={mind2?.id === m.id} accent={theme.accent} onClick={() => setMind2(m)} disabled={!mind1} />
                ))}
              </div>
            </div>
          </div>
          <div className="hidden md:flex justify-center -mt-4 mb-4 relative z-10">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-display text-xs tracking-wider" style={{ background: '#B8860B', color: 'white', boxShadow: '0 4px 16px rgba(184,134,11,0.3)' }}>VS</div>
          </div>
          <div className="mb-6">
            <p className="text-xs font-ui mb-2 tracking-wider uppercase" style={{ color: '#8C8680' }}>Motion for debate</p>
            <input data-testid="debate-topic-input" value={topic} onChange={e => setTopic(e.target.value)} placeholder="This house believes that..."
              className="w-full px-5 py-3.5 rounded-lg text-sm font-body outline-none transition-all"
              style={{ background: 'white', border: '1px solid #E6E1DB', color: '#1A1512' }}
              onFocus={e => e.target.style.borderColor = '#B8860B'} onBlur={e => e.target.style.borderColor = '#E6E1DB'}
            />
          </div>
          <button data-testid="begin-debate-btn" onClick={handleStart} disabled={!mind1 || !mind2 || !topic.trim()}
            className="w-full py-4 font-display text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-2 transition-all cursor-pointer"
            style={{ background: '#1A1512', color: '#FDFCFB', opacity: mind1 && mind2 && topic.trim() ? 1 : 0.35, boxShadow: '0 8px 24px rgba(26,21,18,0.15)' }}>
            <Swords className="w-4 h-4" /> Begin Debate
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MindOption = ({ mind, selected, accent, onClick, disabled }) => (
  <button className="w-full p-2.5 rounded-lg flex items-center gap-3 transition-all cursor-pointer"
    style={{
      background: selected ? 'white' : 'rgba(242,239,233,0.4)',
      border: `1px solid ${selected ? accent : '#E6E1DB'}`,
      boxShadow: selected ? `0 4px 12px ${accent}15` : 'none',
      opacity: disabled ? 0.4 : 1,
    }} onClick={onClick} disabled={disabled}>
    <div className="w-8 h-10 rounded overflow-hidden flex-shrink-0 shadow-sm">
      {mind.portrait ? <img src={mind.portrait} alt={mind.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold" style={{ background: '#F2EFE9', color: accent }}>{mind.name[0]}</div>}
    </div>
    <div className="text-left">
      <p className="font-display text-xs" style={{ color: selected ? accent : '#1A1512' }}>{mind.name}</p>
      <p className="text-[10px] font-body" style={{ color: '#8C8680' }}>{mind.era}</p>
    </div>
  </button>
);

export default RoomInterior;
